export enum DomainReviewEntity {
  Coupon = "coupon",
  Offer = "offer",
  Campaign = "campaign",
  /** campaign_domains row with no campaign_id — legacy brand-level link. */
  BrandLink = "brand_link",
  /** brand_domains registry row. */
  BrandDomain = "brand_domain",
}

export enum DomainReviewAction {
  RemoveDomain = "remove_domain",
  ReplaceDomain = "replace_domain",
  ChangeBrand = "change_brand",
}

export interface IDomainReviewBrand {
  brandId: string;
  name: string | null;
  /** Has a brand_domains row for this domain. */
  registered: boolean;
  registeredValidated: boolean;
  counts: {
    brandDomains: number;
    campaigns: number;
    brandLinks: number;
    couponsDirect: number;
    couponsInherited: number;
    offersDirect: number;
    offersInherited: number;
  };
}

export interface IDomainReviewBrandDomainRow {
  /** brandId|domain|countryCode — brand_domains has no id column. */
  key: string;
  brandId: string;
  brandName: string | null;
  domain: string;
  countryCode: string | null;
  isValidated: boolean;
  validationSource: string | null;
  validatedBy: string | null;
  updatedAt: string | null;
}

export interface IDomainReviewLinkRow {
  /** campaign_domains.id */
  id: string;
  domain: string;
  brandId: string;
  brandName: string | null;
  validated: boolean;
  campaignId: string | null;
  campaignName: string | null;
  /** campaign.brand_id; differs from brandId when the two tables drifted. */
  campaignBrandId: string | null;
  campaignBrandName: string | null;
  publishedStatus: number | null;
  suspended: boolean | null;
  source: string | null;
  overridesFlag: number | null;
  endDate: string | null;
  /** Coupons/offers of this brand served at checkout only through its validated links. */
  inheritedCoupons: number;
  inheritedOffers: number;
  /** False for a "www." spelling, which the lookups never match (the extension strips www.). */
  matchesLookup: boolean;
  /** Campaign is published, in date and not suspended — what the cashback-deal lookup requires. */
  dealLive: boolean;
}

export interface IDomainReviewPromoRow {
  id: string;
  /** Coupon code, or null for offers. */
  code: string | null;
  summary: string | null;
  brandId: string;
  brandName: string | null;
  publishedStatus: number;
  validTo: string | null;
  deactivatedAt: string | null;
  source: string | null;
  overridesFlag: number;
  /** The reviewed domain's spellings this row is linked to through its own domain table. */
  directDomains: string[];
  /** Every domain on the row, for context. */
  allDomains: string[];
  /** Also (or only) matched because its brand has a campaign_domains row for the domain. */
  inherited: boolean;
  /** The checkout lookup returns it: own domain row, or a validated campaign_domains row on its brand. */
  servedAtCheckout: boolean;
  /** Attached to the brand's live cashback deal on this domain; null for offers, which that response doesn't carry. */
  servedWithDeal: boolean | null;
}

export interface IDomainReviewSection<T> {
  rows: T[];
  total: number;
  truncated: boolean;
}

export interface IDomainReviewResponse {
  domain: string;
  variants: string[];
  includeInactive: boolean;
  brandFilter: string | null;
  /** Default reference brands — registered (validated first), else the most-linked brand. */
  suggestedBrandIds: string[];
  brands: IDomainReviewBrand[];
  brandDomains: IDomainReviewBrandDomainRow[];
  campaigns: IDomainReviewSection<IDomainReviewLinkRow>;
  brandLinks: IDomainReviewSection<IDomainReviewLinkRow>;
  coupons: IDomainReviewSection<IDomainReviewPromoRow>;
  offers: IDomainReviewSection<IDomainReviewPromoRow>;
  limit: number;
  /** When these results were read from the database. */
  cachedAt: string;
  /** True when served from the shared cache rather than a fresh database read. */
  fromCache: boolean;
  cacheMinutes: number;
}

export interface IDomainReviewDomainSuggestion {
  domain: string;
  /** campaign_domains rows for this brand and domain that are validated. */
  validated: number;
  /** Rows on campaigns the cashback-deal lookup treats as live. */
  live: number;
  rows: number;
}

export interface IDomainReviewBrandSuggestions {
  brandId: string;
  brandName: string | null;
  suggestions: IDomainReviewDomainSuggestion[];
}

export interface IDomainReviewItemRef {
  entity: DomainReviewEntity;
  /** Row id; for brand_domain the composite key. */
  id: string;
}

export interface IDomainReviewStageRequest {
  domain: string;
  action: DomainReviewAction;
  newDomain?: string;
  newBrandId?: string;
  items: IDomainReviewItemRef[];
}

export type DomainReviewRunStatus =
  | "staged"
  | "pending"
  | "running"
  | "completed"
  | "failed"
  | "cancelled";

/**
 * `invalid` is decided at preview (action doesn't apply to the row);
 * `skipped` at run time (row changed since preview, or a conflict).
 */
export type DomainReviewItemStatus =
  | "pending"
  | "processing"
  | "updated"
  | "no_change"
  | "skipped"
  | "failed"
  | "cancelled"
  | "invalid";

export interface IDomainReviewItem {
  rowNumber: number;
  entity: DomainReviewEntity;
  entityId: string;
  /** Code, campaign name or domain — whatever identifies the row to a person. */
  label: string;
  brandId: string | null;
  brandName: string | null;
  /** What the run will do to this row, in words. */
  plan: string;
  warnings: string[];
  status: DomainReviewItemStatus;
  message: string | null;
  /** Column values the run replaced, kept so a change can be traced and reverted. */
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  processedAt: string | null;
}

export interface IDomainReviewCounts {
  total: number;
  pending: number;
  updated: number;
  noChange: number;
  skipped: number;
  failed: number;
  cancelled: number;
  invalid: number;
}

export interface IDomainReviewRunSummary {
  runId: string;
  domain: string;
  action: DomainReviewAction;
  newDomain: string | null;
  newBrandId: string | null;
  newBrandName: string | null;
  status: DomainReviewRunStatus;
  triggeredBy: string;
  triggeredByName: string | null;
  reason: string | null;
  auditComment: string | null;
  queuedCount: number;
  /** Runs at or under this size are applied inside the confirm request. */
  inline: boolean;
  counts: IDomainReviewCounts;
  stagedAt: string;
  startedAt: string | null;
  completedAt: string | null;
  cancelledBy: string | null;
  errorMessage: string | null;
}

export interface IDomainReviewStageResponse {
  run: IDomainReviewRunSummary;
  items: IDomainReviewItem[];
}

export interface IDomainReviewStartRequest {
  reason: string;
  confirmCount: number;
}

export interface IDomainReviewRunsResponse {
  runs: IDomainReviewRunSummary[];
  total: number;
  page: number;
  pageSize: number;
}

export interface IDomainReviewItemsResponse {
  items: IDomainReviewItem[];
  total: number;
  page: number;
  pageSize: number;
}
